import {
  useWSSnackbar,
  WSPageToolbarAction,
  WSPoster
} from "@wingspanhq/fe-component-library";
import {
  InsuranceCoverageSource,
  InsuranceMonitor,
  InsuranceMonitorStatus,
  PayeeRequirementPayerOverrideStatus
} from "@wingspanhq/wingspan-api-sdk";

import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementContextType,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { getRequirementEngagementsTableCellProps } from "../../../../utils";
import { InsuranceCoverageDetails } from "../../components/InsuranceCoverageDetails";
import { InsuranceCoverageDetailsWidgetContainer } from "../../components/InsuranceCoverageDetailsWidgetContainer";
import { useMutationDownloadInsuranceCertificate } from "../../hooks/useMutationDownloadInsuranceCertificate";
import { selectorIsInsuranceMonitorInProgress } from "../../selectors/selectorIsInsuranceMonitorInProgress";
import { selectorPolicyTypesFromMonitor } from "../../selectors/selectorPolicyTypesFromMonitor";
import { getInsuranceCoveragePayeeRequirementStatus } from "../helpers/getInsuranceCoveragePayeeRequirementStatus";
import { getInsuranceCoverageProgressSteps } from "../helpers/getInsuranceCoverageProgressSteps";
import { getInsuranceCoverageRequirementName } from "../helpers/getInsuranceCoverageRequirementName";
import { INSURANCE_COVERAGE_REQUIREMENT_EVENT } from "../utils";

export const usePayeeInsuranceCoverageConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  InsuranceMonitor | undefined
> => {
  const [downloadInsuranceCertificate] =
    useMutationDownloadInsuranceCertificate();
  const { openSnackbar } = useWSSnackbar();

  const getDownloadInsuranceCoverageFileAction = (
    requirement: RequirementTableItemData,
    insuranceMonitor?: InsuranceMonitor
  ) => {
    // the certificate endpoint 404s unless the linked coverage came from an
    // upload — a Wingspan plan enrollment has no certificate to serve — so the
    // action is offered only for imported coverage, as v1 also required
    if (
      (insuranceMonitor?.status === InsuranceMonitorStatus.FORCE_MATCHED ||
        insuranceMonitor?.status === InsuranceMonitorStatus.COMPLETE) &&
      insuranceMonitor.coverageId &&
      insuranceMonitor.coverage?.sourceType ===
        InsuranceCoverageSource.INSURANCE_IMPORT
    ) {
      const monitorId = insuranceMonitor.id!;

      return async () => {
        await downloadInsuranceCertificate(
          {
            monitorId,
            fileName: `Policy--${monitorId}`
          },
          {
            onSuccess: () => {
              openSnackbar({
                message: "File downloaded successfully",
                type: "success"
              });
              callback(
                INSURANCE_COVERAGE_REQUIREMENT_EVENT.DownloadAsPayer,
                requirement
              );
            },
            onError: () => {
              openSnackbar({
                message: "Download failed",
                type: "warning"
              });
            }
          }
        );
      };
    }

    return undefined;
  };

  return {
    isActionRequired: () => {
      return false;
    },
    requirementName: (requirement, _context, insuranceMonitor) =>
      getInsuranceCoverageRequirementName(
        insuranceMonitor,
        requirement.name || "Insurance Coverage"
      ),

    requirementInstructions:
      (requirement, _context, insuranceMonitor) => (instructions: string) => {
        if (insuranceMonitor?.status === InsuranceMonitorStatus.COMPLETE) {
          return "Your insurance coverage has been submitted successfully.";
        }
        return instructions || "Please provide proof of insurance coverage.";
      },

    requirementDetails: (requirement, context, insuranceMonitor) => {
      const downloadAction = getDownloadInsuranceCoverageFileAction(
        requirement,
        insuranceMonitor
      );

      if (
        requirement.payerOverrideStatus ===
        PayeeRequirementPayerOverrideStatus.PENDING_COMPLETION
      ) {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description="The policy submitted by the contractor was rejected by you."
            insuranceMonitor={insuranceMonitor!}
            pill={{
              theme: "error",
              text: "Rejected",
              icon: true
            }}
            requirement={requirement}
          />
        );
      } else if (insuranceMonitor?.status === InsuranceMonitorStatus.COMPLETE) {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description="A policy from the contractor’s resources matched this requirement."
            insuranceMonitor={insuranceMonitor}
            pill={{
              theme: "success",
              text: "Passed",
              icon: true
            }}
            requirement={requirement}
            onDownloadClick={downloadAction}
          />
        );
      } else if (
        insuranceMonitor?.status === InsuranceMonitorStatus.FORCE_MATCHED
      ) {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description="The contractor submitted a partially matched policy for your approval"
            insuranceMonitor={insuranceMonitor}
            pill={{
              theme: "warning",
              text: "Partial match",
              icon: true
            }}
            requirement={requirement}
            onDownloadClick={downloadAction}
          />
        );
      } else if (
        requirement.payerOverrideStatus ===
          PayeeRequirementPayerOverrideStatus.COMPLETED &&
        selectorIsInsuranceMonitorInProgress(insuranceMonitor)
      ) {
        return {
          header: {
            label: {
              text: requirement.name
            },
            value: {
              pill: {
                theme: "success",
                text: "Approved by you",
                icon: "block"
              }
            }
          },
          data: [],
          children: (
            <WSPoster
              description="Contractor's policy requirement has been marked complete by you"
              title="Policy requirement marked complete by you"
              type="documents"
            />
          )
        };
      } else {
        return {
          header: {
            label: {
              text: requirement.name
            },
            value: {
              pill: {
                theme: "neutral",
                text: "Not started",
                icon: "block"
              }
            }
          },
          data: [],
          children: (
            <WSPoster
              description="Waiting for contractor to provide their proof of insurance."
              title="Awaiting proof of insurance"
              type="documents"
            />
          )
        };
      }
    },
    requirementDetailsProgressWidget: (
      _requirement,
      _context,
      insuranceMonitor
    ) => {
      const steps = getInsuranceCoverageProgressSteps(insuranceMonitor);
      return steps;
    },

    requirementDetailsWidgets: (requirement, context, insuranceMonitor) => {
      return insuranceMonitor ? (
        <InsuranceCoverageDetailsWidgetContainer
          context={RequirementContextType.Payee}
          insuranceMonitor={insuranceMonitor}
        />
      ) : null;
    },

    toolbarActions: (requirement, context, insuranceMonitor) => {
      const downloadAction = getDownloadInsuranceCoverageFileAction(
        requirement,
        insuranceMonitor
      );

      const actions: WSPageToolbarAction[] = [
        {
          buttonKind: "Secondary",
          label: "View policy",
          onAsyncClick: downloadAction,
          visible: !!downloadAction
        }
      ];

      return actions;
    },

    nameCell: (requirement, _context, insuranceMonitor) => ({
      text: getInsuranceCoverageRequirementName(
        insuranceMonitor,
        requirement.name || "Insurance Coverage"
      )
    }),

    typeCell: (requirement, context, insuranceMonitor) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type,
        policyType: selectorPolicyTypesFromMonitor(insuranceMonitor)
      }),

    statusCell: (requirement, context, insuranceMonitor) => {
      return getInsuranceCoveragePayeeRequirementStatus(
        requirement,
        insuranceMonitor
      );
    },
    engagementsCell: getRequirementEngagementsTableCellProps
  };
};
